import { PoolToken } from '@trustpad/launchpad';
import { PoolConfig } from '@trustpad/launchpad/dist/pool/types';
import dayjs from 'dayjs';
import Web3 from 'web3';
import { DateTimeFormat } from '~config/constants';
import { poolsConfig } from '~config/poolsConfig';
import {
  DHDistribution,
  DHUserState,
} from '~features/diamondHands/useDHDistribution';
import { DHReward } from '~features/diamondHands/useDHTreasury';

export const fDate = (unix: number) =>
  dayjs.unix(unix).format(DateTimeFormat.noYear);

export function mapTokenAddressToPool(
  addresses: string[],
): Record<string, PoolConfig> {
  const poolTokens = poolsConfig.reduce((acc, pool) => {
    if (pool.token.address) {
      try {
        acc[Web3.utils.toChecksumAddress(pool.token.address)] = pool;
      } catch (e) {
        acc[pool.token.address] = pool;
      }
    }
    return acc;
  }, {});

  return (
    addresses?.reduce((acc, address) => {
      if (poolTokens[address]) {
        acc[address] = poolTokens[address];
      }
      return acc;
    }, {}) || {}
  );
}

export function mapSaleAddressToPool(
  addresses: string[],
): Record<string, PoolConfig> {
  const pools = poolsConfig.reduce((acc, pool) => {
    if (pool.sale.address) {
      try {
        acc[Web3.utils.toChecksumAddress(pool.sale.address)] = pool;
      } catch (e) {
        acc[pool.sale.address] = pool;
      }
    }
    return acc;
  }, {});

  return addresses.reduce((acc, address) => {
    if (pools[address]) {
      acc[address] = pools[address];
    }
    return acc;
  }, {});
}

const getTokensFromDH = (
  totalReward: number,
  userShare: number,
  totalShare: number,
  isRetroSale: boolean,
  isRetroEligible: boolean,
) => {
  if (isRetroSale && !isRetroEligible) return 0;
  if (!userShare) return 0;
  return totalShare ? totalReward * (userShare / totalShare) : totalReward;
};

export function getUserDHCalculatedRewards(
  distribution: DHDistribution,
  dhUser: DHUserState,
  rewards: DHReward[],
): {
  id: string;
  sale: string;
  token: string;
  isRetroSale: number;
  dh75: number;
  dh25: number;
  total: number;
}[] {
  const saleToPool = mapSaleAddressToPool(distribution.sales);
  const result = [];

  for (const [k, sale] of distribution.sales.entries()) {
    const {
      id,
      token: { address },
    } = saleToPool[sale];
    const reward = rewards.find(
      (r) => r.token.toLowerCase() === address.toLowerCase(),
    );
    const saleIdx = dhUser.sales.findIndex(
      (v) => v.toLowerCase() === sale.toLowerCase(),
    );
    if (!reward) continue;

    const isRetroSale =
      distribution.isRetroSale[distribution.sales.indexOf(sale)];
    const totalDH25Share = isRetroSale
      ? distribution.totalDH25ShareRetro
      : distribution.totalDH25Share;
    const dh25Reward = getTokensFromDH(
      reward.remaining * 0.25,
      dhUser.userDH25Share,
      dhUser.isRegistered
        ? totalDH25Share
        : totalDH25Share + dhUser.userDH25Share,
      isRetroSale,
      dhUser.isRetroEligible,
    );
    // DH75 share is determined in the DHController considering the retro-eligibility status
    const dh75Reward = getTokensFromDH(
      reward.remaining * 0.75,
      dhUser.userDH75Shares[saleIdx],
      distribution.totalDH75Shares[k],
      false,
      false,
    );

    result.push({
      id,
      token: address,
      sale,
      isRetroSale,
      dh75: dh75Reward,
      dh25: dh25Reward,
      total: dh75Reward + dh25Reward,
    });
  }

  return result;
}
